import { School, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Parent } from '../lib/types';
import { Collapsible } from '../ui/Collapsible';
import { CloseOutSettings } from './CloseOut';
import { DocumentsSettings } from './Documents';
import { FamilyScheduleSettings } from './FamilySchedule';
import { useParent } from './ParentContext';
import { ReportPeriodsSettings } from './ReportPeriods';
import { SchoolCalendar } from './SchoolCalendar';

function timeZones(): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return [Intl.DateTimeFormat().resolvedOptions().timeZone];
  }
}

// The profile and school sections of Settings. They were the Family Account page (#317) until it merged into Settings
// (#496); old /family-account links redirect there.

/** The family's school hours, school year, holidays and breaks, and report periods. */
export function SchoolSettings() {
  return (
    <Collapsible id="school-settings" title="School Settings" icon={<School size={18} />} anchors={['family-schedule', 'school-calendar', 'state-requirements', 'holidays', 'report-periods', 'close-year', 'documents']}>
      <p className="muted small">Your family’s school hours, school year, holidays and breaks, and when report cards come out.</p>
      <FamilyScheduleSettings embedded />
      <SchoolCalendar embedded />
      <CloseOutSettings embedded />
      <ReportPeriodsSettings embedded />
      <DocumentsSettings embedded />
    </Collapsible>
  );
}

/** The signed-in parent's own details: their name (the kids see it) and time zone. */
export function GuardianProfile() {
  const { parent, setParent, toast } = useParent();
  const [name, setName] = useState(parent.name);
  const [tz, setTz] = useState(parent.timezone);
  const dirty = name.trim() !== parent.name || tz !== parent.timezone;
  const zones = timeZones();

  async function save() {
    try {
      setParent(await api.patch<Parent>('/api/me', { name: name.trim(), timezone: tz }));
      toast({ text: 'Saved.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <Collapsible id="account" title="Your profile" icon={<UserRound size={18} />}>
      <p className="muted small">Signed in as {parent.email}</p>
      <label>
        Your name <span className="muted small">(your kids see it when you watch or talk)</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
      </label>
      <label>
        Time zone for school hours
        <select value={tz} onChange={(e) => setTz(e.target.value)}>
          {!zones.includes(tz) && <option value={tz}>{tz}</option>}
          {zones.map((z) => (
            <option key={z} value={z}>
              {z.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </label>
      <button className="btn primary" onClick={save} disabled={!dirty || !name.trim()}>
        Save
      </button>
      <PaperworkDetails />
    </Collapsible>
  );
}

interface MyPaperwork {
  used: boolean;
  legalName: string;
  homeAddress: string;
  phone: string;
}

/**
 * The primary parent's details for state paperwork (#954): shown only to a family owner whose family has state
 * compliance on, and deleted when it's turned off.
 */
function PaperworkDetails() {
  const { toast } = useParent();
  const [saved, setSaved] = useState<MyPaperwork | null>(null);
  const [form, setForm] = useState<MyPaperwork | null>(null);
  useEffect(() => {
    api
      .get<MyPaperwork>('/api/compliance/me')
      .then((d) => {
        setSaved(d);
        setForm(d);
      })
      .catch(() => undefined);
  }, []);
  if (!saved?.used || !form) return null;
  const dirty = form.legalName !== saved.legalName || form.homeAddress !== saved.homeAddress || form.phone !== saved.phone;

  async function save() {
    try {
      const d = await api.put<MyPaperwork>('/api/compliance/me', { legalName: form!.legalName.trim(), homeAddress: form!.homeAddress.trim(), phone: form!.phone.trim() });
      setSaved(d);
      setForm(d);
      toast({ text: 'Saved.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <div className="stack state-paperwork">
      <div className="row-between wrap">
        <h3>For state paperwork</h3>
        <span className="tag">Primary parent</span>
      </div>
      <p className="muted small">
        Printed in the “From” part of your notices and reports. Kept only while your family uses state compliance, and deleted when it’s turned off.
      </p>
      <label>
        Your legal name
        <input value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} maxLength={120} autoComplete="name" />
      </label>
      <div className="field-row">
        <label className="grow">
          Home address
          <textarea rows={2} value={form.homeAddress} onChange={(e) => setForm({ ...form, homeAddress: e.target.value })} maxLength={300} placeholder="Street, city, ZIP" autoComplete="street-address" />
        </label>
        <label>
          Phone <span className="muted small">(optional)</span>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} maxLength={40} autoComplete="tel" />
        </label>
      </div>
      <div>
        <button className="btn primary" onClick={save} disabled={!dirty}>
          Save
        </button>
      </div>
    </div>
  );
}
